import { setAdoc_t } from "../../../redux/reducer/userSlice";
import { IonContent, IonImg, IonPage, IonSpinner } from "@ionic/react";
import dietaSocialLogo from "../../../assets/images/ds-logo.svg";
import { useDispatch } from "react-redux";
import { useEffect } from "react";
import { useHistory } from "react-router";
import {
  getUserInfo,
  resetUserInfoThrottle,
} from "../../../redux/actions/userActions";
import { fetchAppPermissions } from "../../../redux/actions/permissionsActions";
import { clearPersistedChatUnread } from "../../../redux/chatUnreadPersist";
import {
  beginImpersonatedSession,
  endImpersonatedSession,
  getAuthToken,
} from "../../../services/authStorage";
import {
  isOnboardingIncomplete,
  onboardingResumePath,
  hydrateGoalsCatalogFromApi,
  hydrateInitialConfigFromProfile,
} from "../../../services/onboardingProfile";

const normalizeBool = (v) => v === true || v === 1 || v === "1";

/**
 * Decode the base64 token carried in the URL.
 *
 * `Buffer` is a Node global: webpack 5 stopped shipping it to the browser and nothing
 * here polyfills it, so the previous `Buffer.from(...)` threw a ReferenceError, the
 * catch below swallowed it, and both this page and /login/:token dropped the visitor
 * on the login screen with no explanation. `atob` is the browser's own decoder;
 * `Buffer` is kept as a fallback for any runtime that provides it instead.
 */
const decodeToken = (value) => {
  if (typeof atob === "function") {
    return atob(value);
  }

  if (typeof Buffer !== "undefined") {
    return Buffer.from(value, "base64").toString("ascii");
  }

  throw new Error("Nessun decoder base64 disponibile.");
};

/**
 * How long to keep asking for the profile before giving up.
 *
 * The API answers 403 on every permission-checked endpoint for a few seconds at
 * a time — see PermissionCacheService::can(), where any failure in the
 * permission lookup is swallowed and turned into a denial. The hand-off used to
 * make one attempt, fail, and land on a loader until some unrelated poller
 * happened to refetch fifteen or twenty seconds later. Retrying on our own
 * schedule turns that wait into a blink.
 */
const PROFILE_RETRY_DELAYS_MS = [400, 900, 1800, 3000, 5000];

const fetchProfileWithRetry = async (dispatch, isCancelled) => {
  let lastError;

  for (let attempt = 0; attempt <= PROFILE_RETRY_DELAYS_MS.length; attempt++) {
    if (isCancelled()) throw new Error("Hand-off annullato.");

    try {
      // Every attempt must reach the network: getUserInfo throttles, and
      // piggybacks on an in-flight call, so a retry would otherwise return the
      // same failure it just saw.
      resetUserInfoThrottle();
      return await dispatch(getUserInfo()).unwrap();
    } catch (error) {
      lastError = error;

      const delay = PROFILE_RETRY_DELAYS_MS[attempt];
      if (delay === undefined) break;

      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
};

const GetTokenPage = ({ match, impersonate }) => {
  const dispatch = useDispatch();
  const token = match.params.token;
  const history = useHistory();

  // No IonLoading overlay here, deliberately.
  //
  // Ionic resolves `present()` through requestAnimationFrame, and a browser
  // does not run animation frames for a tab that is not being painted. So in a
  // tab opened in the background — which is exactly how the CRM hands an
  // account over — `await presentLoading(...)` never returned and the whole
  // hand-off stalled on its first line: no token stored, no profile fetched,
  // just the spinner, until the operator happened to focus the tab. The page
  // renders its own loading state below, so the overlay was decoration
  // sitting in front of the work.
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      let decodedToken;
      try {
        // Decode token from URL param (base64)
        decodedToken = decodeToken(token);
        if (!decodedToken) throw new Error("Token non valido.");

        if (impersonate) {
          // A support session belongs to THIS TAB only. localStorage is shared by
          // every tab on this origin, so an operator who is also signed in here
          // had their own tab writing its token and profile back over the
          // impersonated ones — they ended up looking at their own account, and
          // the CRM started answering 401. sessionStorage is per-tab, so the two
          // sessions cannot reach each other.
          beginImpersonatedSession(decodedToken);
          // The banner mounts with the app, before this runs, and a storage write
          // raises no event in the tab that made it — without this it would stay
          // hidden for the whole session.
          window.dispatchEvent(new Event("impersonation-changed"));
        } else {
          // Same page serves /login/:token — a real sign-in, which belongs to the
          // browser rather than the tab. Clear the previous occupant's cached
          // identity first: `adoc_p` seeds the redux user slice at module load, so
          // leaving it behind shows the wrong person until the profile arrives.
          endImpersonatedSession();
          localStorage.removeItem("adoc_p");
          localStorage.removeItem("adoc_agreements");
          clearPersistedChatUnread();
          localStorage.setItem("adoc_t", decodedToken);
          localStorage.removeItem("adoc_i");
          // The banner read the flag when it mounted, a moment before this ran,
          // so signing in through a magic link in a tab that had been a support
          // session left "Stai vedendo l'app come..." on screen for the member.
          window.dispatchEvent(new Event("impersonation-changed"));
        }
        dispatch(setAdoc_t(decodedToken));

        // Fetch profile + agreements (and cache them) before deciding onboarding gate
        const profile = await fetchProfileWithRetry(dispatch, () => cancelled);
        dispatch(fetchAppPermissions()).catch(() => {});

        if (cancelled) return;

        // An operator opening an account from the CRM must never be put through
        // onboarding: they are there to look at the account, and completing that form
        // would write the operator's answers into the member's own profile. Members
        // whose onboarding is genuinely unfinished are exactly the ones support is
        // asked about, so this case is common rather than rare.
        const incomplete = !impersonate && isOnboardingIncomplete(profile);

        if (incomplete) {
          try { const maybe = hydrateGoalsCatalogFromApi(dispatch); if (maybe?.unwrap) await maybe.unwrap(); } catch {}
          hydrateInitialConfigFromProfile(dispatch, profile);
        }

        const nextRoute = incomplete ? onboardingResumePath(profile) : "/home";
        history.replace(nextRoute, { login: true });
      } catch (e) {
        if (!cancelled) {
          // The session may already be established: the token is stored before the
          // profile is fetched, so a failure in the steps after it (hydrating the
          // onboarding config, for one) used to drop a perfectly signed-in visitor
          // on the login screen. Only send them there if there is really no session.
          const signedIn = !!getAuthToken();
          history.replace(signedIn ? "/home" : "/login", { login: signedIn });
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [token, impersonate, dispatch, history]);

  return (
    <IonPage className="loginPage">
      <IonContent fullscreen>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            gap: "24px",
            height: "75vh",
          }}
        >
          <IonImg src={dietaSocialLogo} />
          <IonSpinner name="crescent" />
          <p style={{ margin: 0, color: "var(--ion-color-medium, #7a7a7a)" }}>
            Attendere prego...
          </p>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default GetTokenPage;
